import { useEffect, useMemo, useState } from "react";
import type {
    DefaultRawData,
    IGenericChart,
    PeriodOverPeriodRawData,
    RawData,
} from "app/pages/industry-analysis/segmentation/size-and-growth-overview/common/types";
import Chart from "pro-features/components/Chart/src/Chart";
import type { ColumnChartData } from "pro-features/components/Chart/src/types";
import { getSelectedCategory } from "app/pages/industry-analysis/common/getSelectedCategory";
import styled from "styled-components";
import { NoData } from "app/pages/keyword-analysis/TotalTrafficPage/Components/UtilityComponents";
import { LoadingSpinner } from "app/pages/keyword-analysis/TotalTrafficPage/StyledComponents";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";
import { genericDecorator } from "app/UtilitiesAndConstants/UtilityFunctions/genericDecorator";
import { fetchData } from "../../common/fetchData";

interface IChartWrapper<T> extends IGenericChart<T> {
    chartType?: string;
    endpoint?: string;
    parseRawData: (rawData: RawData) => ColumnChartData;
    // eslint-disable-next-line @typescript-eslint/no-wrapper-object-types
    getChartConfig: (chartData: ColumnChartData) => Object;
}

const ENDPOINT = "api/Industries/Benchmark/barchart";

const Container = styled.div`
    padding: 20px;
    /* set z index in order to display the tooltip on top of custom data labels */
    .highcharts-tooltip {
        z-index: 1;
    }
`;

const LoaderContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 340px;
`;

const ChartWrapper = (props) => {
    const {
        chartType = chartTypes.COLUMN,
        chartState,
        endpoint = ENDPOINT,
        getChartConfig,
        parseRawData,
        setChartState,
    } = props;
    const { rawData } = chartState;
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const category = getSelectedCategory();
    useEffect(() => {
        setIsLoading(true);
        fetchData<RawData>(endpoint)
            .then((rawData) => {
                setChartState({ ...chartState, rawData });
            })
            .finally(() => setIsLoading(false));
    }, [category.id, category.categoryHash]);
    const chartData = useMemo(() => {
        if (!rawData) {return [];}
        return parseRawData(rawData);
    }, [rawData, isLoading]);
    const chartConfig = useMemo(() => {
        return getChartConfig(chartData);
    }, [chartData]);

    if (isLoading)
        {return (
            <LoaderContainer>
                <LoadingSpinner />
            </LoaderContainer>
        );}
    const isNoData = false;
    if (isNoData) {
        return (
            <NoData
                paddingTop="80px"
                noDataTitleKey="global.nodata.notavilable"
                noDataSubTitleKey="workspaces.marketing.nodata.subtitle"
            />
        );
    }
    if (isLoading) {
        return (
            <LoaderContainer>
                <LoadingSpinner />
            </LoaderContainer>
        );
    }
    return (
        <Container>
            <Chart
                type={chartType}
                data={chartData}
                config={chartConfig}
            />
        </Container>
    );
};

type DefaultChartWrapperType = IChartWrapper<DefaultRawData>;
export const DefaultChartWrapper = genericDecorator<DefaultChartWrapperType>(ChartWrapper);

type PeriodOverPeriodChartWrapperType = IChartWrapper<PeriodOverPeriodRawData>;
export const PeriodOverPeriodChartWrapper = genericDecorator<PeriodOverPeriodChartWrapperType>(ChartWrapper);
